import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import apiClient from '../utils/api'

export interface User {
  id: string
  email?: string | null
  phone?: string | null
  username?: string | null
  role: 'student' | 'teacher' | 'admin'
  firstName?: string | null
  lastName?: string | null
  avatar?: string | null
  status: string
  emailVerified: boolean
  phoneVerified: boolean
  createdAt: Date
  updatedAt: Date
}

interface AuthStore {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitializing: boolean
  error: string | null
  
  // Actions
  login: (email: string, password: string) => Promise<void>
  setUser: (user: User) => void
  setTokens: (token: string, refreshToken: string) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  updateUser: (updates: Partial<User>) => void
  initialize: () => void
  setError: (error: string | null) => void
  clearAll: () => void
}

const API_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isInitializing: true,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await axios.post(`${API_URL}/auth/login`, {
            email,
            password
          })
          
          // 处理不同的响应结构
          const payload = response.data?.data ?? response.data
          const user = payload?.user ?? payload?.profile ?? payload
          const token = payload?.token ?? response.data?.token
          const refreshToken = payload?.refreshToken ?? response.data?.refreshToken
          
          if (!user || !token) {
            throw new Error('登录响应格式错误')
          }
          
          set({
            user,
            token,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
          
          // 设置默认请求头
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          localStorage.setItem('token', token)
        } catch (error) {
          console.error('登录失败:', error)
          set({ isLoading: false, error: error instanceof Error ? error.message : 'An error occurred' })
          throw error
        }
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true })
      },

      setTokens: (token: string, refreshToken: string) => {
        set({ token, refreshToken })
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      },

      logout: async () => {
        const currentToken = get().token
        const currentRefreshToken = get().refreshToken

        // 先清除本地状态
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          isLoading: false
        })
        
        // 清除 localStorage 中的 token
        localStorage.removeItem('token')
        localStorage.removeItem('auth-store')
        
        // 清除 axios 默认请求头
        delete axios.defaults.headers.common['Authorization']

        // 通知后端吊销token（异步执行，不阻塞退出流程）
        if (currentToken) {
          try {
            await axios.post(`${API_URL}/auth/logout`, {
              refreshToken: currentRefreshToken
            }, {
              headers: { Authorization: `Bearer ${currentToken}` }
            })
          } catch (error) {
            // 静默处理，即使后端调用失败也不影响前端退出
            console.warn('退出登录时后端调用失败:', error)
          }
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      updateUser: (updates: Partial<User>) => {
        const currentUser = get().user
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } })
        }
      },

      initialize: () => {
        const token = localStorage.getItem('token')
        
        if (token) {
          // 重要：在验证期间先设置为未认证状态，避免显示虚假的登录状态
          set({ 
            token, 
            isAuthenticated: false,  // 先设置为false，验证成功后再设置为true
            isInitializing: true,
            user: null,  // 清除可能的旧用户数据
            error: null
          })
          
          // 设置axios默认请求头
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          // 通过获取用户信息来验证token有效性
          axios.get(`${API_URL}/users/profile`)
          .then(response => {
            const payload = response.data?.data ?? response.data
            const profile = payload?.user ?? payload?.profile ?? payload
            if (profile && profile.id) {
              set({ 
                user: profile, 
                isAuthenticated: true,  // 验证成功后才设置为true
                isInitializing: false,
                error: null
              })
            } else {
              throw new Error('用户信息获取失败')
            }
          })
          .catch((error) => {
            // 验证失败，完全清除认证状态
            set({ 
              user: null, 
              token: null, 
              refreshToken: null,
              isAuthenticated: false, 
              isInitializing: false,
              error: 'Token已过期，请重新登录'
            })
            localStorage.removeItem('token')
            localStorage.removeItem('auth-store')  // 清除持久化数据
            delete axios.defaults.headers.common['Authorization']
          })
        } else {
          // 没有token，确保是完全的未登录状态
          set({ 
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false, 
            isInitializing: false,
            error: null
          })
        }
      },

      setError: (error: string | null) => {
        set({ error })
      },

      // 新增：完全清除所有认证状态，回到初始未登录状态
      clearAll: () => {
        // 清除内存中的状态
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          isLoading: false,
          isInitializing: false
        })
        
        // 清除本地存储
        localStorage.removeItem('token')
        localStorage.removeItem('auth-store')  // 清除zustand持久化数据
        
        // 清除axios默认请求头
        delete axios.defaults.headers.common['Authorization']
      }
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken
        // 不要持久化 isAuthenticated，这个状态应该每次都重新验证
      })
    }
  )
) 